import BigNumber from 'bignumber.js'
import Web3 from 'web3'
import { Interface } from '@ethersproject/abi'
import { GAS_PRICE } from 'config'
import abiSep20Factory from 'config/abi/sep20Factory.json'
import abiSep20Template from 'config/abi/iSep20Template.json'
import abiSep20FactoryTokenBasic from 'config/abi/sep20FactoryTokenBasic.json'
import abiSep20FactoryTokenLiquidityGenerator from 'config/abi/sep20FactoryTokenLiquidityGenerator.json'
import abiSep20FactoryTokenAnnuity from 'config/abi/sep20FactoryTokenAnnuity.json'
import abiSep20FactoryTokenMultiMint from 'config/abi/sep20FactoryTokenMultiMint.json'
import abiSep20FactoryTokenInscriptions from 'config/abi/sep20FactoryTokenInscriptions.json'
import multicall from 'utils/multicall'
import { 
    getSep20FactoryAddress, getSep20TemplateBasicAddress, getNullAddress, getTokenBurnerAddress,
    getGenesisAddress, getSep20TemplateLiquidityGeneratorAddress, getSep20TemplateAnnuityAddress, getSep20TemplateMultiMintAddress, getSep20TemplateInscriptionsAddress
} from 'utils/addressHelpers'

const gasPriceInObj = GAS_PRICE?{gasPrice:GAS_PRICE}:{};

export const getBasicSep20Tokens = async (addresses, user = undefined) => {
    const sep20Tokens = [];
    
    // Calls1: Get token info
    const calls1 = [];
    for(let j=0; j<addresses.length; j++) {
        sep20Tokens.push({address: addresses[j]});
        calls1.push({
            address: addresses[j],
            name: 'name'
        }, {
            address: addresses[j],
            name: 'symbol'
        }, {
            address: addresses[j],
            name: 'decimals'
        }, {
            address: addresses[j],
            name: 'totalSupply'
        }, {
            address: addresses[j],
            name: 'owner'
        }, {
            address: addresses[j],
            name: 'isMintable'
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getNullAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getGenesisAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getTokenBurnerAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [addresses[j]]
        });

        if (user) {
            calls1.push({
                address: addresses[j],
                name: 'balanceOf',
                params: [user]
            });
        }
    }

    // Handle result for call1
    const c1pl = user ? 11 : 10;
    const res1 = await multicall(abiSep20FactoryTokenBasic, calls1);
    for(let k=0; k<addresses.length; k++) {
        Object.assign(sep20Tokens[k], {
            name: res1[k*c1pl][0],
            symbol: res1[k*c1pl+1][0],
            decimals: new BigNumber(res1[k*c1pl+2][0]).toNumber(),
            supply: new BigNumber(res1[k*c1pl+3][0]._hex),
            owner: res1[k*c1pl+4][0],
            isMintable: res1[k*c1pl+5][0],
            burned: new BigNumber(res1[k*c1pl+6][0]._hex).plus(res1[k*c1pl+7][0]._hex).plus(res1[k*c1pl+8][0]._hex).plus(res1[k*c1pl+9][0]._hex)
        });
        sep20Tokens[k].supply = sep20Tokens[k].supply.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].burned = sep20Tokens[k].burned.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].circulating = sep20Tokens[k].supply.minus(sep20Tokens[k].burned);

        if (user) {
            sep20Tokens[k].userBalance = new BigNumber(res1[k*c1pl+10][0]._hex).div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        }
    }

    return sep20Tokens;
}

export const getLiquidityGeneratorSep20Tokens = async (addresses, user = undefined) => {
    const sep20Tokens = [];
    
    // Calls1: Get token info
    const calls1 = [];
    for(let j=0; j<addresses.length; j++) {
        sep20Tokens.push({address: addresses[j]});
        calls1.push({
            address: addresses[j],
            name: 'name'
        }, {
            address: addresses[j],
            name: 'symbol'
        }, {
            address: addresses[j],
            name: 'totalSupply'
        }, {
            address: addresses[j],
            name: 'owner'
        }, {
            address: addresses[j],
            name: '_taxFee'
        }, {
            address: addresses[j],
            name: '_liquidityFee'
        }, {
            address: addresses[j],
            name: '_charityFee'
        }, {
            address: addresses[j],
            name: '_charityAddress'
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getNullAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getGenesisAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getTokenBurnerAddress()]
        });
        
        if (user) {
            calls1.push({
                address: addresses[j],
                name: 'balanceOf',
                params: [user]
            });
        }
    }

    // Handle result for call1
    const c1pl = user ? 12 : 11;
    const res1 = await multicall(abiSep20FactoryTokenLiquidityGenerator, calls1);
    for(let k=0; k<addresses.length; k++) {
        Object.assign(sep20Tokens[k], {
            name: res1[k*c1pl][0],
            symbol: res1[k*c1pl+1][0],
            decimals: 9,
            supply: new BigNumber(res1[k*c1pl+2][0]._hex),
            owner: res1[k*c1pl+3][0],
            taxFeeRate: new BigNumber(res1[k*c1pl+4][0]._hex).div(100).toNumber(),
            liquidityFeeRate: new BigNumber(res1[k*c1pl+5][0]._hex).div(100).toNumber(),
            charityFeeRate: new BigNumber(res1[k*c1pl+6][0]._hex).div(100).toNumber(),
            charityAddress: res1[k*c1pl+7][0],
            burned: new BigNumber(res1[k*c1pl+8][0]._hex).plus(res1[k*c1pl+9][0]._hex).plus(res1[k*c1pl+10][0]._hex)
        });
        sep20Tokens[k].supply = sep20Tokens[k].supply.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].burned = sep20Tokens[k].burned.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].circulating = sep20Tokens[k].supply.minus(sep20Tokens[k].burned);
        
        if (user) {
            sep20Tokens[k].userBalance = new BigNumber(res1[k*c1pl+11][0]._hex).div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        }
    }

    return sep20Tokens;
}

export const getAnnuitySep20Tokens = async (addresses, user=undefined) => {
    const sep20Tokens = [];
    
    // Calls1: Get token info
    const calls1 = [];
    for(let j=0; j<addresses.length; j++) {
        sep20Tokens.push({address: addresses[j]});
        calls1.push({
            address: addresses[j],
            name: 'name'
        }, {
            address: addresses[j],
            name: 'symbol'
        }, {
            address: addresses[j],
            name: 'getApyBP'
        }, {
            address: addresses[j],
            name: 'getPayoutPeriod'
        }, {
            address: addresses[j],
            name: 'getEndInterestTime'
        }, {
            address: addresses[j],
            name: 'getCirculatingSupply'
        }, {
            address: addresses[j],
            name: 'getTotalStakingSupply'
        }, {
            address: addresses[j],
            name: 'getTotalPayoutSupply'
        }, {
            address: addresses[j],
            name: 'getFullyDilutedSupply'
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getNullAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getGenesisAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getTokenBurnerAddress()]
        });
                
        if (user) {
            calls1.push({
                address: addresses[j],
                name: 'balanceOf',
                params: [user]
            }, {
                address: addresses[j],
                name: 'getStatus',
                params: [user]
            }, {
                address: addresses[j],
                name: 'getAccountBalance',
                params: [user]
            }, {
                address: addresses[j],
                name: 'getLastProcessedTime',
                params: [user]
            }, {
                address: addresses[j],
                name: 'getLastStakingBalance',
                params: [user]
            });
        }
    }

    // Handle result for call1
    const c1pl = user ? 17 : 12;
    const res1 = await multicall(abiSep20FactoryTokenAnnuity, calls1);
    const timestampNow = Math.floor(Number(new Date())/1000);
    for(let k=0; k<addresses.length; k++) {
        Object.assign(sep20Tokens[k], {
            name: res1[k*c1pl][0],
            symbol: res1[k*c1pl+1][0],
            decimals: 18,
            apy: new BigNumber(res1[k*c1pl+2][0]).div(100).toNumber(),
            payoutPeriodInDays: new BigNumber(res1[k*c1pl+3][0]._hex).div(86400).toNumber(),
            endInterestTime: new BigNumber(res1[k*c1pl+4][0]._hex).toNumber(),
            totalStaked: new BigNumber(res1[k*c1pl+6][0]._hex).div(new BigNumber(10).pow(18)),
            totalInPayout: new BigNumber(res1[k*c1pl+7][0]._hex).div(new BigNumber(10).pow(18)),
            supply: new BigNumber(res1[k*c1pl+8][0]._hex).div(new BigNumber(10).pow(18)),
            burned: new BigNumber(res1[k*c1pl+9][0]._hex).plus(res1[k*c1pl+10][0]._hex).plus(res1[k*c1pl+11][0]._hex).div(new BigNumber(10).pow(18))
        });
        sep20Tokens[k].circulating = new BigNumber(res1[k*c1pl+5][0]._hex).div(new BigNumber(10).pow(18)).minus(sep20Tokens[k].burned);

        if (user) {
            sep20Tokens[k].userBalance = new BigNumber(res1[k*c1pl+12][0]._hex).div(new BigNumber(10).pow(18));
            sep20Tokens[k].status = new BigNumber(res1[k*c1pl+13][0]).toNumber();
            sep20Tokens[k].accountBalance = new BigNumber(res1[k*c1pl+14][0]._hex).div(new BigNumber(10).pow(18));
            sep20Tokens[k].lastProcessedTime = new BigNumber(res1[k*c1pl+15][0]._hex).toNumber();
            sep20Tokens[k].lastStakingBalance = new BigNumber(res1[k*c1pl+16][0]._hex).div(new BigNumber(10).pow(18));

            sep20Tokens[k].currentEstimate = new BigNumber(0); 
            if (sep20Tokens[k].accountBalance.isGreaterThan(0)) {
                // calculate current estimate
                if (sep20Tokens[k].status === 0) {  
                    // Payout Mode
                    const timePassedInSeconds = new BigNumber(timestampNow).minus(sep20Tokens[k].lastProcessedTime);
                    if (timePassedInSeconds.isGreaterThan(0)) {
                        const payoutPeriodPercentage = timePassedInSeconds.div(res1[k*c1pl+3][0]._hex);
                        const maxPayoutAmount = payoutPeriodPercentage.times(sep20Tokens[k].lastStakingBalance);
                        sep20Tokens[k].currentEstimate = maxPayoutAmount.isGreaterThan(sep20Tokens[k].accountBalance) ? sep20Tokens[k].accountBalance : maxPayoutAmount;
                    }
                } else {    
                    // Staking Mode
                    const timePassedInSeconds = new BigNumber(Math.min(timestampNow, sep20Tokens[k].endInterestTime)).minus(sep20Tokens[k].lastProcessedTime);
                    if (timePassedInSeconds.isGreaterThan(0)) {
                        const percentageYearStaked = timePassedInSeconds.div(31536000);
                        const compoundedRate = new BigNumber(res1[k*c1pl+2][0]).plus(10000).div(10000).toNumber()**percentageYearStaked.toNumber();
                        sep20Tokens[k].currentEstimate = sep20Tokens[k].accountBalance.times(compoundedRate);
                    }
                }
            }
        }
    }

    return sep20Tokens;
}

export const getMultiMintSep20Tokens = async (addresses, user = undefined) => {
    const sep20Tokens = [];
    
    // Calls1: Get token info
    const calls1 = [];
    for(let j=0; j<addresses.length; j++) {
        sep20Tokens.push({address: addresses[j]});
        calls1.push({
            address: addresses[j],
            name: 'name'
        }, {
            address: addresses[j],
            name: 'symbol'
        }, {
            address: addresses[j],
            name: 'decimals'
        }, {
            address: addresses[j],
            name: 'totalSupply'
        }, {
            address: addresses[j],
            name: 'owner'
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getNullAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getGenesisAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getTokenBurnerAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [addresses[j]]
        });

        if (user) {
            calls1.push({
                address: addresses[j],
                name: 'balanceOf',
                params: [user]
            }, {
                address: addresses[j],
                name: 'allowanceToMint',
                params: [user]
            });
        }
    }

    // Handle result for call1
    const c1pl = user ? 11 : 9;
    const res1 = await multicall(abiSep20FactoryTokenMultiMint, calls1);
    for(let k=0; k<addresses.length; k++) {
        Object.assign(sep20Tokens[k], {
            name: res1[k*c1pl][0],
            symbol: res1[k*c1pl+1][0],
            decimals: new BigNumber(res1[k*c1pl+2][0]).toNumber(),
            supply: new BigNumber(res1[k*c1pl+3][0]._hex),
            owner: res1[k*c1pl+4][0],
            burned: new BigNumber(res1[k*c1pl+5][0]._hex).plus(res1[k*c1pl+6][0]._hex).plus(res1[k*c1pl+7][0]._hex).plus(res1[k*c1pl+8][0]._hex)
        });
        sep20Tokens[k].supply = sep20Tokens[k].supply.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].burned = sep20Tokens[k].burned.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].circulating = sep20Tokens[k].supply.minus(sep20Tokens[k].burned);

        if (user) {
            sep20Tokens[k].userBalance = new BigNumber(res1[k*c1pl+9][0]._hex).div(new BigNumber(10).pow(sep20Tokens[k].decimals));
            sep20Tokens[k].userAllowanceToMint = new BigNumber(res1[k*c1pl+10][0]._hex).div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        }
    }

    return sep20Tokens;
}

export const getInscriptionsSep20Tokens = async (addresses, user = undefined) => {
    const sep20Tokens = [];
    
    // Calls1: Get token info
    const calls1 = [];
    for(let j=0; j<addresses.length; j++) {
        sep20Tokens.push({address: addresses[j]});
        calls1.push({
            address: addresses[j],
            name: 'name'
        }, {
            address: addresses[j],
            name: 'symbol'
        }, {
            address: addresses[j],
            name: 'decimals'
        }, {
            address: addresses[j],
            name: 'totalSupply'
        }, {
            address: addresses[j],
            name: 'mintCap'
        }, {
            address: addresses[j],
            name: 'supplyCap'
        }, {
            address: addresses[j],
            name: 'mintAmount'
        }, {
            address: addresses[j],
            name: 'mintFee'
        }, {
            address: addresses[j],
            name: 'IsCanonical'
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getNullAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getGenesisAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [getTokenBurnerAddress()]
        }, {
            address: addresses[j],
            name: 'balanceOf',
            params: [addresses[j]]
        });

        if (user) {
            calls1.push({
                address: addresses[j],
                name: 'balanceOf',
                params: [user]
            },{
                address: addresses[j],
                name: 'getMintCount',
                params: [user]
            });
        }
    }

    // Handle result for call1
    const c1pl = user ? 15 : 13;
    const res1 = await multicall(abiSep20FactoryTokenInscriptions, calls1);
    for(let k=0; k<addresses.length; k++) {
        Object.assign(sep20Tokens[k], {
            name: res1[k*c1pl][0],
            symbol: res1[k*c1pl+1][0],
            decimals: new BigNumber(res1[k*c1pl+2][0]).toNumber(),
            supply: new BigNumber(res1[k*c1pl+3][0]._hex),
            mintCap: new BigNumber(res1[k*c1pl+4][0]._hex),
            supplyCap: new BigNumber(res1[k*c1pl+5][0]._hex),
            mintAmount: new BigNumber(res1[k*c1pl+6][0]._hex),
            mintFee: new BigNumber(res1[k*c1pl+7][0]._hex).div(new BigNumber(10).pow(18)),
            isCanonical: res1[k*c1pl+8][0],
            burned: new BigNumber(res1[k*c1pl+9][0]._hex).plus(res1[k*c1pl+10][0]._hex).plus(res1[k*c1pl+11][0]._hex).plus(res1[k*c1pl+12][0]._hex)
        });
        sep20Tokens[k].supply = sep20Tokens[k].supply.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].burned = sep20Tokens[k].burned.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].circulating = sep20Tokens[k].supply.minus(sep20Tokens[k].burned);
        sep20Tokens[k].supplyCap = sep20Tokens[k].supplyCap.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].mintAmount = sep20Tokens[k].mintAmount.div(new BigNumber(10).pow(sep20Tokens[k].decimals));
        sep20Tokens[k].isMintable = false;

        if (user) {
            sep20Tokens[k].userBalance = new BigNumber(res1[k*c1pl+13][0]._hex).div(new BigNumber(10).pow(sep20Tokens[k].decimals));
            sep20Tokens[k].mintCount = new BigNumber(res1[k*c1pl+14][0]._hex).toNumber();
            sep20Tokens[k].isMintable = (sep20Tokens[k].mintCap.isEqualTo(0) || sep20Tokens[k].mintCap.isGreaterThan(sep20Tokens[k].mintCount)) 
                && sep20Tokens[k].supply.plus(sep20Tokens[k].mintAmount).isLessThanOrEqualTo(sep20Tokens[k].supplyCap);
        }
    }

    return sep20Tokens;
}

const TEMPLATE_TYPE = {
    [getSep20TemplateBasicAddress().toLowerCase()]: {
        name: "Basic",
        abi: abiSep20FactoryTokenBasic,
        getData: getBasicSep20Tokens
    },
    [getSep20TemplateLiquidityGeneratorAddress().toLowerCase()]: {
        name: "LiquidityGenerator",
        abi: abiSep20FactoryTokenLiquidityGenerator,
        getData: getLiquidityGeneratorSep20Tokens
    },
    [getSep20TemplateAnnuityAddress().toLowerCase()]: {
        name: "Annuity",
        abi: abiSep20FactoryTokenAnnuity,
        getData: getAnnuitySep20Tokens
    },
    [getSep20TemplateMultiMintAddress().toLowerCase()]: {
        name: "MultiMint",
        abi: abiSep20FactoryTokenMultiMint,
        getData: getMultiMintSep20Tokens
    },
    [getSep20TemplateInscriptionsAddress().toLowerCase()]: {
        name: "Inscriptions",
        abi: abiSep20FactoryTokenInscriptions,
        getData: getInscriptionsSep20Tokens
    }
};

export const getCreationFee = async (sep20FactoryContract) => {
    return sep20FactoryContract.methods.creationFee().call()
}

export const getFeeToken = async (sep20FactoryContract) => {
    return sep20FactoryContract.methods.feeToken().call()
}

export const getCreator = async (sep20FactoryContract, sep20TokenAddress) => {
    return sep20FactoryContract.methods.getCreator(sep20TokenAddress).call()
}

export const getSep20CountByCreator = async (sep20FactoryContract, userAddress) => {
    return sep20FactoryContract.methods.getSep20Count(userAddress).call()
}

export const getSep20Count = async (sep20FactoryContract) => {
    return sep20FactoryContract.methods.count().call()
}

export const getSep20Template = async (sep20FactoryContract, sep20TokenAddress) => {
    return sep20FactoryContract.methods.getTemplate(sep20TokenAddress).call()
}

export const getTemplateType = (templateAddress) => {
    return TEMPLATE_TYPE[templateAddress.toLowerCase()]?.name || "Unknown";
}

export const createSep20 = async (sep20FactoryContract, templateAddress, account, callData) => {
    try {
        return sep20FactoryContract.methods
            .createSep20(templateAddress, account, callData)
            .send({ ...gasPriceInObj, from: account})
            .on('transactionHash', (tx) => {
                return tx.transactionHash
            })
    } catch (err) {
        return console.error(err)
    }
}

export const createSep20TokenBasic = async (sep20FactoryContract, account, name, symbol, decimals, initialSupply, isMintable) => {
    const itf = new Interface(abiSep20FactoryTokenBasic);
    const callData = itf.encodeFunctionData("initialize", [account, name, symbol, decimals, initialSupply, isMintable]);

    return createSep20(sep20FactoryContract, getSep20TemplateBasicAddress(), account, `0x${callData.substring(10)}`);
}

export const createSep20TokenLiquidityProvider = async (sep20FactoryContract, account, name, symbol, supply, charityAddress, taxFeeBp, liquidityFeeBp, charityFeeBp) => {
    const itf = new Interface(abiSep20FactoryTokenLiquidityGenerator);
    const callData = itf.encodeFunctionData("initialize", [account, name, symbol, supply, charityAddress, taxFeeBp, liquidityFeeBp, charityFeeBp]);

    return createSep20(sep20FactoryContract, getSep20TemplateLiquidityGeneratorAddress(), account, `0x${callData.substring(10)}`);
}

export const createSep20TokenAnnuity = async (sep20FactoryContract, account, name, symbol, supply, payoutPeriod, endInterestTime, apyBP) => {
    const itf = new Interface(abiSep20FactoryTokenAnnuity);
    const callData = itf.encodeFunctionData("initialize", [account, name, symbol, supply, payoutPeriod, endInterestTime, apyBP]);

    return createSep20(sep20FactoryContract, getSep20TemplateAnnuityAddress(), account, `0x${callData.substring(10)}`);
}

export const createSep20TokenMultiMint = async (sep20FactoryContract, account, name, symbol, decimals, initialSupply) => {
    const itf = new Interface(abiSep20FactoryTokenMultiMint);
    const callData = itf.encodeFunctionData("initialize", [account, name, symbol, decimals, initialSupply]);

    return createSep20(sep20FactoryContract, getSep20TemplateMultiMintAddress(), account, `0x${callData.substring(10)}`);
}

export const createSep20TokenInscriptions = async (sep20FactoryContract, account, name, symbol, decimals, supplyCap, mintAmount, mintCap, mintFee) => {
    const itf = new Interface(abiSep20FactoryTokenInscriptions);
    const callData = itf.encodeFunctionData("initialize", [name, symbol, decimals, supplyCap, mintAmount, mintCap, mintFee]);

    return createSep20(sep20FactoryContract, getSep20TemplateInscriptionsAddress(), account, `0x${callData.substring(10)}`);
}

export const LiquidityGeneratorLiquidityAddress = "0x10159b39FaBECd32f2603F14909f255F0c677dC0";

export const checkLiquidityGeneratorWhiteList = async (sep20LiquidityGeneratorTokenContract, address) => {
    if (Web3.utils.isAddress(address)) {
        return sep20LiquidityGeneratorTokenContract.methods.isExcludedFromFee(address).call();
    }
    return false;
}

export const checkMultiMintAddressStatus = async (sep20MultiMintTokenContract, address) => {
    if (Web3.utils.isAddress(address)) {
        try {
            const allowance = await sep20MultiMintTokenContract.methods.allowanceToMint(address).call();
            const totalMinted = await sep20MultiMintTokenContract.methods.totalMinted(address).call();
     
            return {
                a: address,
                l: allowance,
                m: totalMinted
            };
        } catch(e) {
            console.error(e);
        }
    }
    return false;
}

export const addToLiquidityGeneratorWhitelist = async (sep20LiquidityGeneratorTokenContract, account, address) => {
    try {
        return sep20LiquidityGeneratorTokenContract.methods
            .excludeFromFee(address)
            .send({ ...gasPriceInObj, from: account})
            .on('transactionHash', (tx) => {
                return tx.transactionHash
            })
    } catch (err) {
        return console.error(err)
    }
}

export const removeFromLiquidityGeneratorWhitelist = async (sep20LiquidityGeneratorTokenContract, account, address) => {
    try {
        return sep20LiquidityGeneratorTokenContract.methods
            .includeInFee(address)
            .send({ ...gasPriceInObj, from: account})
            .on('transactionHash', (tx) => {
                return tx.transactionHash
            })
    } catch (err) {
        return console.error(err)
    }
}

export const stakeToAnnuity = async (tokenContract, account, amount) => {
    try {
        return tokenContract.methods
        .increaseBalanceAndStake(amount)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const annuitySwitchToPayout = async (tokenContract, account) => {
    try {
        return tokenContract.methods
        .startPayout()
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const annuitySwitchToStaking = async (tokenContract, account) => {
    try {
        return tokenContract.methods
        .startStake()
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const annuityCollectPayout = async (tokenContract, account) => {
    try {
        return tokenContract.methods
        .collectPayout()
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const annuityForcePayout = async (tokenContract, account, amount) => {
    try {
        return tokenContract.methods
        .forcePayout(amount, 1)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const multiMintChangeAllowanceToMint = async (tokenContract, account, minter, amount) => {
    try {
        return tokenContract.methods
        .setAllowanceToMint(minter, amount)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const mintInscriptions = async (tokenContract, account) => {
    try {
        return tokenContract.methods
        .mint()
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const getTokenAddressFromQuery = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
}

export const getMySep20Tokens = async (sep20FactoryContract, userAddress, currentPage = 1) => {
    const maxInPage = 12;
    try {
        const count = await getSep20CountByCreator(sep20FactoryContract, userAddress);
        const pageCount = Math.ceil(count/maxInPage);
        const curPage = currentPage > pageCount ? 1 : currentPage;

        const calls1 = [];
        for(let i=(count-1-(curPage-1)*maxInPage); i>=Math.max(0,(count-curPage*maxInPage)); i--) {
            calls1.push({
                address: sep20FactoryContract.options.address,
                name: 'getSep20s',
                params: [userAddress, i],
            });
        }
        const res1 = await multicall(abiSep20Factory, calls1);

        const tokens = await getSep20Tokens(sep20FactoryContract, res1.map(r=>r[0]), userAddress);

        return tokens === false ? false : {
            currentPage: curPage,
            pageCount,
            tokens: tokens || [],
            isLoading: false
        };
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const getRecentSep20Tokens = async (sep20FactoryContract, currentPage = 1, user = undefined) => {
    const maxInPage = 12;
    try {
        const count = await getSep20Count(sep20FactoryContract);
        const pageCount = Math.ceil(count/maxInPage);
        const curPage = currentPage > pageCount ? 1 : currentPage;

        const calls1 = [];
        for(let i=(count-1-(curPage-1)*maxInPage); i>=Math.max(0,(count-curPage*maxInPage)); i--) {
            calls1.push({
                address: sep20FactoryContract.options.address,
                name: 'getSep20ByIndex',
                params: [i],
            });
        }
        const res1 = await multicall(abiSep20Factory, calls1);
        const arr = res1 && res1.map(r=>r[0]) || [];
        const all = {};
        arr.forEach((r)=>{
            all[r]=true;
        });
        if (singleSep20Token) all[singleSep20Token] = true;
        const allArray = Object.keys(all);
        allArray.forEach((r,i)=>{
            all[r] = i;
        });

        const hasAccount = user && (new BigNumber(user.toLowerCase())).isGreaterThan(0);
        const tokens = await getSep20Tokens(sep20FactoryContract, allArray, hasAccount ? user : undefined);

        return tokens === false ? false : {
            currentPage: curPage,
            pageCount,
            count,
            tokens: tokens && arr.map(r=>tokens[all[r]]).filter(r=>r) || [],
            isLoading: false
        };
    } catch (e) {
        console.error(e);
        return false;
    }
}

let singleSep20Token = null;

export const getSingleSep20Token = async (sep20FactoryContract, address, user = undefined) => {
    if (CACHE && CACHE[address] && CACHE[address].timestamp && CACHE[address].timestamp + 20000 >= Date.now()) {
        singleSep20Token = address;
        return [CACHE[address].data];
    }

    let addresses = [address];
    try {
        const creator = await getCreator(sep20FactoryContract, address);
        if (creator && new BigNumber(creator.toLowerCase()).toNumber() <= 0) addresses = [];
    } catch {
        addresses = [];
    }

    if (!addresses.length) return [];

    singleSep20Token = address;

    const hasAccount = user && (new BigNumber(user.toLowerCase())).isGreaterThan(0);
    return getSep20Tokens(sep20FactoryContract, addresses, hasAccount ? user : undefined);
}

const CACHE = {};

export const getSep20Tokens = async (sep20FactoryContract, addresses, user = undefined) => {
    try {
        const sep20Tokens = [];
        const templates = {};
        const indexes = {};
        addresses.forEach((a,i) => {
            indexes[a]= i;
        });

        // Call1: Get token types
        const calls1 = [];
        for(let j=0; j<addresses.length; j++) {
            sep20Tokens.push({address: addresses[j]});
            calls1.push({
                address: sep20FactoryContract.options.address,
                name: 'getTemplate',
                params: [addresses[j]],
            },{
                address: sep20FactoryContract.options.address,
                name: 'getCreator',
                params: [addresses[j]],
            });
        }

        // Handle result for call1
        const res1 = await multicall(abiSep20Factory, calls1);
        for(let m=0; m<addresses.length; m++) {
            sep20Tokens[m].type = getTemplateType(res1[m*2][0]);
            if (!templates[res1[m*2][0]]) templates[res1[m*2][0]] = [];
            templates[res1[m*2][0]].push(addresses[m]);
            sep20Tokens[m].creator = res1[m*2+1][0];
        }

        // Retriving data based on template types
        const tasks = [];
        Object.keys(templates).forEach((ta) => {
            const arr = templates[ta];
            const dataGetter = TEMPLATE_TYPE[ta.toLowerCase()]?.getData;
            if (dataGetter) {
                tasks.push(
                    dataGetter(arr, user).then((tokens)=>{
                        tokens.forEach(token => {
                            Object.assign(sep20Tokens[indexes[token.address]], token);
                        });
                    })
                );
            }
        });
        await Promise.all(tasks);

        const filtered = sep20Tokens.filter(t => t.name);

        // Update Cache
        filtered.forEach((e) => {
            CACHE[e.address] = {
                data: e,
                timestamp: Date.now()
            }
        });

        return filtered;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const mint = async (tokenContract, to, account) => {
    try {
        return tokenContract.methods
        .mint(to)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const transferOwnership = async (tokenContract, newOwner, account) => {
    try {
        return tokenContract.methods
        .transferOwnership(newOwner)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}
